import type { SendMemberInviteRequest } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import * as memberAccessApi from "@/lib/api/flow-instances/member-access";
import { flowInstanceQueryKeys } from "@/lib/hooks/use-flow-instances/use-flow-instances";
import { inviteRecipientKeys } from "@/lib/hooks/use-flow-instances/use-invite-recipient";

export const memberAccessKeys = {
  list: (instanceId: string | null) => ["flowInstances", instanceId, "member-access"] as const,
};

/**
 * QL-505 follow-up — a member-link mutation, and the group's own access-link
 * generate/revoke (the prerequisite for member links), can flip the Invite
 * dialog's recipient (e.g. the last unlinked member becomes linkable) and
 * touch the same public-access surface, so the member-access list, the
 * recipient and the instance metadata are invalidated together.
 */
export function useInvalidateInviteSurface(instanceId: string) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: memberAccessKeys.list(instanceId) });
    queryClient.invalidateQueries({ queryKey: inviteRecipientKeys.value(instanceId) });
    queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.metadata(instanceId) });
  }, [queryClient, instanceId]);
}

/** QL-505 — the current answering parties (QL-654: group members, or 2+ representatives) + their link status. */
export function useMemberAccess(instanceId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: memberAccessKeys.list(instanceId),
    queryFn: () => memberAccessApi.getMemberAccess(instanceId!),
    enabled: !!instanceId,
  });
  return { members: data?.members ?? [], error, isLoading };
}

export function useIssueMemberLink(instanceId: string) {
  const invalidateInviteSurface = useInvalidateInviteSurface(instanceId);
  return useMutation({
    mutationFn: (subjectId: string) => memberAccessApi.issueMemberLink(instanceId, subjectId),
    onSuccess: invalidateInviteSurface,
  });
}

export function useRevokeMemberLink(instanceId: string) {
  const invalidateInviteSurface = useInvalidateInviteSurface(instanceId);
  return useMutation({
    mutationFn: (subjectId: string) => memberAccessApi.revokeMemberLink(instanceId, subjectId),
    onSuccess: invalidateInviteSurface,
  });
}

export function useSendMemberInvite(instanceId: string) {
  const invalidateInviteSurface = useInvalidateInviteSurface(instanceId);
  return useMutation({
    mutationFn: ({
      subjectId,
      channel,
    }: {
      subjectId: string;
      channel: SendMemberInviteRequest["channel"];
    }) => memberAccessApi.sendMemberInvite(instanceId, subjectId, channel),
    onSuccess: invalidateInviteSurface,
  });
}
